import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, Input, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { CourseStructureCategory } from 'app/library/course-offerings-category/models/course-structure-category.model';
import { QualificationCategory } from 'app/library/qualifications/qualification-category/models/qualification-category.model';
import { Team } from 'app/library/team/models/team.model';
import { IEntityNode } from '../../../interfaces/entity-node.interface';

export const MOVE_TO_ROOT_DESTINATION_ID = '__MOVE_TO_ROOT__';

@Component({
    selector: 'app-move-to-dialog',
    templateUrl: './move-to-dialog.component.html',
    styleUrls: ['./move-to-dialog.component.scss'],
})
export class MoveToDialogComponent implements OnInit {
    private _listTree: IEntityNode[];
    private _entity: Team | MaxBrainAudience | CourseStructureCategory | QualificationCategory;

    @Input()
    set listTree(value: IEntityNode[]) {
        this._listTree = this.markBlockedDestinations(value || []);
        this.dataSource.data = this._listTree;
    }

    get listTree(): IEntityNode[] {
        return this._listTree;
    }

    @Input()
    set entity(value: Team | MaxBrainAudience | CourseStructureCategory | QualificationCategory) {
        this._entity = value;
        if (this._listTree?.length) {
            this._listTree = this.markBlockedDestinations(this._listTree);
            this.dataSource.data = this._listTree;
        }
    }

    get entity(): Team | MaxBrainAudience | CourseStructureCategory | QualificationCategory {
        return this._entity;
    }

    @Input()
    showAvatar = true;

    @Input()
    emptyIcon = 'team_illustration';

    @Input()
    emptyText = 'TEAMS.DIALOG.MOVE_TO.NO_TEAMS';

    @Input()
    title: string;

    @Input()
    isLoading = false;

    @Input()
    lazyLoadChildren: (node: IEntityNode) => Promise<IEntityNode[]>;

    @Input()
    allowMoveToRoot = false;

    treeControl = new NestedTreeControl<IEntityNode>((node) => node.children);
    dataSource = new MatTreeNestedDataSource<IEntityNode>();
    selectedNode: IEntityNode;
    shimmerRows = [1, 2, 3, 4, 5];
    childShimmerRows = [1, 2, 3];
    readonly moveToRootDestinationId = MOVE_TO_ROOT_DESTINATION_ID;

    constructor(public dialogRef: MatDialogRef<MoveToDialogComponent>) {}

    ngOnInit(): void {
        if (this._listTree) {
            this.dataSource.data = this._listTree;
        }
    }

    get isRootSelected(): boolean {
        return this.selectedNode?.id === this.moveToRootDestinationId;
    }

    get hasContent(): boolean {
        return !!this.listTree?.length || this.allowMoveToRoot;
    }

    hasChild = (_: number, node: IEntityNode) => (!!node.children && node.children.length > 0) || (!!this.lazyLoadChildren && (node.childrenCount || 0) > 0);

    async toggleNode(node: IEntityNode): Promise<void> {
        if (this.treeControl.isExpanded(node)) {
            this.treeControl.collapse(node);
            return;
        }

        if (this.lazyLoadChildren && node.childrenCount > 0 && (!node.children || node.children.length === 0)) {
            node.isLoading = true;
            this.treeControl.expand(node);

            const children = await this.lazyLoadChildren(node);
            const parentBlocked = node.isBlockedDestination || node.id === this._entity?.id;
            node.children = this.markBlockedDestinations(children || [], parentBlocked);
            node.childrenCount = node.children.length;

            node.isLoading = false;
            this.refreshDataSource();
            this.treeControl.expand(node);
            return;
        }

        this.treeControl.expand(node);
    }

    onSelectRoot(): void {
        if (this.selectedNode && this.selectedNode.id !== this.moveToRootDestinationId) {
            this.selectedNode.isSelected = false;
        }

        this.selectedNode = {
            id: this.moveToRootDestinationId,
            name: '',
            isSelected: true,
        };
    }

    onDeselectRoot(): void {
        this.selectedNode = null;
    }

    onSelect(node: IEntityNode): void {
        if (node.isBlockedDestination) {
            return;
        }

        if (this.selectedNode && this.selectedNode.id !== this.moveToRootDestinationId) {
            this.selectedNode.isSelected = false;
        }

        node.isSelected = true;
        this.selectedNode = node;
    }

    onDeselect(node: IEntityNode): void {
        node.isSelected = false;
        this.selectedNode = null;
    }

    onClearSelection(): void {
        if (!this.selectedNode) {
            return;
        }

        if (this.selectedNode.id === this.moveToRootDestinationId) {
            this.onDeselectRoot();
            return;
        }

        this.onDeselect(this.selectedNode);
    }

    onSave(): void {
        this.dialogRef.close(this.selectedNode);
    }

    private markBlockedDestinations(nodes: IEntityNode[], parentBlocked = false): IEntityNode[] {
        return nodes.map((node) => {
            const isBlockedDestination = parentBlocked || node.id === this._entity?.id;

            return {
                ...node,
                isBlockedDestination,
                children: node.children?.length ? this.markBlockedDestinations(node.children, isBlockedDestination) : node.children || [],
            };
        });
    }

    private refreshDataSource(): void {
        const data = this.dataSource.data;
        this.dataSource.data = null;
        this.dataSource.data = data;
    }
}
